import { api } from '../client';
import type { CurrentUser, LoginResult, MfaSetup, MfaVerification } from '../types';

/** POST /api/user/newacc (apps/web api/auth.ts SignupRequest). */
export interface SignupRequest {
  email_address: string;
  name: string | null;
  surname: string | null;
  account_name: string;
  analytics_event_id: string;
  /** PostHog's id for the visitor; only with analytics consent. */
  analytics_distinct_id: string | null;
  funnel_journey_id: string;
}

/** The created owner; the verification page needs its id. */
export type SignupResult = Pick<CurrentUser, 'id' | 'email_address'>;

export const authApi = {
  login: (credentials: { email_address: string; password: string }, signal?: AbortSignal) =>
    api.post<LoginResult>('/api/user/login', credentials, { signal }),

  /** A refused token answers 401 — an outcome to report on the page, not a redirect. */
  redeemSupportSession: (token: string) =>
    api.post<LoginResult>('/api/user/support-session', { token }, { skipAuthRedirect: true }),

  getMfaSetup: (signal?: AbortSignal) => api.get<MfaSetup>('/api/user/mfa/setup', { signal }),

  verifyMfa: (code: string, signal?: AbortSignal) =>
    api.post<MfaVerification>('/api/user/mfa/verify', { code }, { signal }),

  signup: (body: SignupRequest, signal?: AbortSignal) => api.post<SignupResult>('/api/user/newacc', body, { signal }),

  /** Sets the password, confirms the account and starts a session (auth cookie). */
  confirm: (body: { user_id: string; signup_secret: string; password: string }, signal?: AbortSignal) =>
    api.post<undefined>('/api/user/confirm', body, { signal }),

  /** Always answers 202: the server never says whether the address has an account. */
  requestPasswordReset: (emailAddress: string, signal?: AbortSignal) =>
    api.post<undefined>('/api/user/send-reset', { email_address: emailAddress }, { signal }),

  resetPassword: (body: { user_id: string; reset_password_secret: string; password: string }, signal?: AbortSignal) =>
    api.post<undefined>('/api/user/reset-password', body, { signal }),

  resendConfirmation: (emailAddress: string) =>
    api.post<undefined>('/api/user/resend-confirmation', { email_address: emailAddress }),

  logout: () => api.get<undefined>('/api/user/logout', { skipAuthRedirect: true }),

  /**
   * Full-page navigation: the OAuth handshake needs the browser to visit
   * Google. Signup passes its analytics ids so the server-side conversion
   * joins the funnel (apps/web GoogleAuthButton.vue).
   */
  googleLoginUrl: (analytics?: { analytics_event_id: string; funnel_journey_id: string; analytics_distinct_id: string | null }): string => {
    const url = new URL('/api/auth/google/login', window.location.origin);
    if (analytics) {
      url.searchParams.set('analytics_event_id', analytics.analytics_event_id);
      url.searchParams.set('funnel_journey_id', analytics.funnel_journey_id);
      if (analytics.analytics_distinct_id) url.searchParams.set('analytics_distinct_id', analytics.analytics_distinct_id);
    }
    return url.href;
  },
};
